"use client";
import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useContactModal } from '@/contexts/ContactModalContext';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';
const CARD = '#fbfdfd';

interface PersonaNode {
    label: string;
    sub: string;
    count: number;
    angle: number;
}

const personas: PersonaNode[] = [
    { label: 'CMO', sub: 'SaaS & Technology', count: 128, angle: -90 },
    { label: 'VP Sales', sub: '200+ Employees', count: 94, angle: -18 },
    { label: 'Head of Growth', sub: 'Marketing & Growth', count: 156, angle: 54 },
    { label: 'IT Director', sub: 'Enterprise', count: 61, angle: 126 },
    { label: 'COO', sub: 'VP & Above', count: 87, angle: 198 },
];

const RADAR_CENTER = 190;
const RADAR_RADIUS = 140;

function toRadarPercent(angle: number) {
    const rad = (angle * Math.PI) / 180;
    return {
        left: ((RADAR_CENTER + RADAR_RADIUS * Math.cos(rad)) / 380) * 100,
        top: ((RADAR_CENTER + RADAR_RADIUS * Math.sin(rad)) / 380) * 100,
    };
}

function DecisionMakerRadar() {
    const totalMatches = personas.reduce((sum, p) => sum + p.count, 0);

    return (
        <div className="relative w-full max-w-[420px] mx-auto lg:mx-0">
            <div className="relative aspect-square">
                {/* Slowly rotating scan ring */}
                <motion.svg
                    viewBox="0 0 380 380"
                    className="absolute inset-0 w-full h-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 50, repeat: Infinity, ease: 'linear' }}
                >
                    <circle cx={RADAR_CENTER} cy={RADAR_CENTER} r={168} fill="none" stroke="rgba(55,48,163,0.16)" strokeWidth="1.5" strokeDasharray="2 9" strokeLinecap="round" />
                </motion.svg>

                {/* Static ring + connector lines */}
                <svg viewBox="0 0 380 380" className="absolute inset-0 w-full h-full">
                    <circle cx={RADAR_CENTER} cy={RADAR_CENTER} r={108} fill="none" stroke="rgba(55,48,163,0.1)" strokeWidth="1" />
                    {personas.map((p, i) => {
                        const rad = (p.angle * Math.PI) / 180;
                        const x = RADAR_CENTER + RADAR_RADIUS * Math.cos(rad);
                        const y = RADAR_CENTER + RADAR_RADIUS * Math.sin(rad);
                        return (
                            <motion.line
                                key={p.label}
                                x1={RADAR_CENTER}
                                y1={RADAR_CENTER}
                                x2={x}
                                y2={y}
                                stroke="rgba(55,48,163,0.28)"
                                strokeWidth="1.2"
                                strokeDasharray="3 5"
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.3 + i * 0.12 }}
                            />
                        );
                    })}
                </svg>

                {/* Center match score */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.7 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="absolute rounded-full flex flex-col items-center justify-center text-center"
                    style={{
                        width: '32%',
                        height: '32%',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: INK,
                    }}
                >
                    <motion.span
                        className="absolute inset-0 rounded-full"
                        style={{ border: `1.5px solid ${LIME}` }}
                        animate={{ scale: [1, 1.4], opacity: [0.55, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                    />
                    <span className="font-bold leading-none" style={{ fontFamily: CLASH, color: LIME, fontSize: 'clamp(20px, 2.4vw, 28px)' }}>
                        {totalMatches}
                    </span>
                    <span className="text-[9px] font-mono uppercase tracking-wide mt-1" style={{ color: 'rgba(251,253,253,0.6)' }}>
                        matched
                    </span>
                </motion.div>

                {/* Persona nodes */}
                {personas.map((p, i) => {
                    const { left, top } = toRadarPercent(p.angle);
                    return (
                        <motion.div
                            key={p.label}
                            initial={{ opacity: 0, scale: 0.6 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.45, delay: 0.5 + i * 0.12, ease: 'easeOut' }}
                            className="absolute flex items-center justify-center rounded-full text-center px-1"
                            style={{
                                left: `${left}%`,
                                top: `${top}%`,
                                transform: 'translate(-50%, -50%)',
                                width: 60,
                                height: 60,
                                background: CARD,
                                border: `1.5px solid ${PURPLE}`,
                                boxShadow: '0 10px 24px -12px rgba(8,4,0,0.25)',
                            }}
                            title={`${p.sub} — ${p.count} matched`}
                        >
                            <span className="text-[10.5px] font-bold leading-tight" style={{ fontFamily: CLASH, color: INK }}>
                                {p.label}
                            </span>
                            <span
                                className="absolute -bottom-1.5 -right-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none"
                                style={{ background: LIME, color: INK, fontFamily: CLASH }}
                            >
                                {p.count}
                            </span>
                        </motion.div>
                    );
                })}
            </div>

            {/* Summary stat strip */}
            <motion.div
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 1.2 }}
                className="flex items-center justify-center gap-2 mt-7 flex-wrap"
            >
                {[
                    { label: 'Buying Roles', value: `${personas.length}` },
                    { label: 'Profiles Mapped', value: totalMatches.toLocaleString() },
                    { label: 'Relevance Score', value: '94%' },
                ].map((s) => (
                    <div
                        key={s.label}
                        className="flex flex-col items-center px-4 py-2.5 rounded-2xl"
                        style={{ background: 'rgba(55,48,163,0.05)', border: '1px solid rgba(55,48,163,0.14)' }}
                    >
                        <span className="text-sm font-bold" style={{ fontFamily: CLASH, color: PURPLE }}>
                            {s.value}
                        </span>
                        <span className="text-[9px] font-mono uppercase tracking-wide" style={{ color: 'rgba(8,4,0,0.4)' }}>
                            {s.label}
                        </span>
                    </div>
                ))}
            </motion.div>
        </div>
    );
}

const tools = [
    { src: '/logos/linkedin.svg', alt: 'LinkedIn Campaign Manager' },
    { src: '/logos/analytics.svg', alt: 'Google Analytics' },
    { src: '/logos/hubspot.svg', alt: 'HubSpot' },
];

export default function LinkedInAdsHero() {
    const { openModal } = useContactModal();

    return (
        <section className="relative bg-white pt-0 pb-0 overflow-hidden">
            <div
                className="relative w-full px-6 md:px-16 lg:px-24 pt-36 pb-24 overflow-hidden"
                style={{ background: '#ffffff' }}
            >
                <div
                    className="absolute inset-0 opacity-[0.05] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }}
                />
                <div
                    className="absolute pointer-events-none"
                    style={{
                        top: '-22%', right: '-14%', width: 640, height: 580,
                        background: 'radial-gradient(circle, rgba(55,48,163,0.13) 0%, transparent 70%)',
                        borderRadius: '58% 42% 37% 63% / 46% 39% 61% 54%',
                    }}
                />
                <div className="max-w-[1360px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative">

                    <div className="lg:col-span-7 flex flex-col items-start gap-7 text-left relative">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="flex items-center gap-2 text-xs md:text-sm font-medium"
                            style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.45)' }}
                        >
                            <Link href="/services/performance-marketing" className="hover:underline" style={{ color: 'rgba(8,4,0,0.45)' }}>
                                Performance Marketing
                            </Link>
                            <span>/</span>
                            <span style={{ color: PURPLE }}>LinkedIn Ads</span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="text-4xl md:text-5xl lg:text-[3.3rem] font-bold leading-[1.1] tracking-tight"
                            style={{ fontFamily: CLASH, color: INK }}
                        >
                            LinkedIn Ads for B2B Brands That Need to Reach the{' '}
                            <span style={{ color: PURPLE }}>Right Decision-Makers</span>
                        </motion.h1>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.22 }}
                            className="flex flex-col gap-4 max-w-xl"
                        >
                            <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.65)', letterSpacing: '0.01em' }}>
                                B2B advertising isn&apos;t about reaching the largest audience. It&apos;s about reaching the right people inside the right companies.
                            </p>
                            <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.65)', letterSpacing: '0.01em' }}>
                                Lead Workz builds LinkedIn advertising campaigns around professional audiences, company characteristics, buying groups and business objectives — from lead generation to account-based campaigns.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.32 }}
                            className="pt-1"
                        >
                            <button
                                onClick={openModal}
                                className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                                style={{ background: PURPLE, color: '#ffffff', fontFamily: CLASH, letterSpacing: '0.08em' }}
                            >
                                Build My LinkedIn Ads Strategy →
                            </button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                            className="flex items-center gap-4 pt-3"
                        >
                            <span className="text-xs font-semibold uppercase tracking-[0.1em]" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.35)' }}>
                                Measured with
                            </span>
                            <div className="flex items-center gap-3">
                                {tools.map((t) => (
                                    <div key={t.alt} className="w-8 h-8 grayscale opacity-50 hover:opacity-100 hover:grayscale-0 transition-all duration-300">
                                        <Image src={t.src} alt={t.alt} width={32} height={32} className="w-full h-full object-contain" />
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    <div className="lg:col-span-5">
                        <DecisionMakerRadar />
                    </div>

                </div>
            </div>
        </section>
    );
}
